import React from 'react';
import { User, Brain, Compass, Building2, Users, TrendingUp } from 'lucide-react';
import { problemPoints } from '../data/mock';
import { schoolProblemPoints } from '../data/audienceContent';
import { CDN } from '../data/cdnImages';
import { useAudience } from '../contexts/AudienceContext';
import { BrandName } from './BrandName';

const studentIcons = {
  User,
  Brain,
  Compass,
};

const schoolIcons = {
  Users,
  Building2,
  TrendingUp,
};

const ProblemSection = () => {
  const { isSchool } = useAudience();

  if (isSchool) {
    return (
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto mb-8">
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              What principals and heads ask before they say yes
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              You need career guidance that feels consistent in every section, gentle on counsellor time, and easy to
              explain when parents or the board ask for receipts. Here is how we plug into what you already run.
            </p>
          </div>

          <p className="text-center text-lg font-semibold text-teal-800 mb-10 max-w-3xl mx-auto">
            One Career DNA spine, every batch, evidence you can show without reinventing your program overnight
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {schoolProblemPoints.map((point) => {
              const IconComponent = schoolIcons[point.icon];
              return (
                <div
                  key={point.id}
                  className="group relative bg-gradient-to-br from-gray-50 to-teal-50/40 p-8 rounded-2xl border border-gray-200 hover:border-teal-300 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                    <IconComponent className="w-7 h-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{point.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-16 max-w-4xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-xl border border-blue-100">
              <img
                src={CDN.problemSchool}
                alt="Students and educators in a modern classroom"
                width={1200}
                height={600}
                loading="lazy"
                decoding="async"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 via-blue-900/25 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <p className="text-2xl font-semibold">
                    &ldquo;When guidance sounds the same in every classroom, families stop bouncing between
                    contradictory advice.&rdquo;
                  </p>
                  <p className="text-teal-200 mt-2">
                    Partner schools working with <BrandName variant="onDark" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-4xl mx-auto mb-8">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Find what fits you, not just a popular one
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Most students are guided towards a few common careers. We help you step back, understand yourself better, and explore 100+ career options that actually fit, so your choice feels right, not forced.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {problemPoints.map((point) => {
            const IconComponent = studentIcons[point.icon];
            return (
              <div
                key={point.id}
                className="group relative bg-gradient-to-br from-gray-50 to-blue-50/50 p-8 rounded-2xl border border-gray-200 hover:border-blue-300 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                  <IconComponent className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h3>
                <p className="text-gray-600 leading-relaxed">{point.description}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-16 max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden shadow-xl">
            <img
              src={CDN.problemStudent}
              alt="Student reflecting on career choices"
              width={1200}
              height={600}
              loading="lazy"
              decoding="async"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-gray-900/30 to-transparent flex items-end">
              <div className="p-8 text-white">
                <p className="text-2xl font-semibold">
                  &ldquo;I wish someone had shown me the right direction earlier...&rdquo;
                </p>
                <p className="text-blue-200 mt-2">A feeling many students share quietly</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
